import { BaseElement } from 'base';
import { product } from 'product';
import { sectionFragment } from 'section-fragment';
import Swiper from 'swiper-core';

const TOGGLE_TAG = 'action-button';
const WIDE = '(min-width: 768px)';
const HOVER = '(hover: hover) and (pointer: fine)';
const REDUCE = '(prefers-reduced-motion: reduce)';
const PAUSED = 'paused';
const PLAYING = 'playing';
const PLAY_STATES = [PAUSED, PLAYING];
const PROGRESS = '--workout-benefits-progress';
const GAP = '--workout-benefits-gap';
const BLEED = '--workout-benefits-bleed';
const PHOTO_DURATION = 3000;
const SLIDE_SPEED = 300;
const CARD_IN_VIEW = 0.6;
const SECTION_IN_VIEW = 0.25;
const STALL_TIMEOUT = 4000;
const BUFFERING_DELAY = 250;
const ADVANCE_RETRIES = 120;
const LOAD_EVENTS = ['loadstart', 'progress', 'suspend', 'canplay'];
const VARIANT_TOPIC = 'product:variant';

const isAbort = error => error?.name === 'AbortError';

class WorkoutBenefits extends BaseElement {
  #viewport = null;
  #swiper = null;
  #wideMq = null;
  #hoverMq = null;
  #reduceMq = null;
  #cards = [];
  #videos = new Map();
  #toggles = new Map();
  #fills = new Map();
  #storyFills = [];
  #storiesToggle = null;
  #prev = null;
  #next = null;
  #locked = new Set();
  #manual = new Set();
  #inView = new Set();
  #failed = new Set();
  #unplayable = new Set();
  #offScreen = true;
  #sectionShown = false;
  #hidden = false;
  #frame = 0;
  #stories = false;
  #storyIndex = 0;
  #storyUserPaused = false;
  #photoElapsed = 0;
  #photoStartedAt = 0;
  #pendingAdvance = false;
  #advanceRetries = 0;
  #stallTime = -1;
  #stallSince = 0;
  #bufferingSince = 0;
  #bufferingTimer = 0;
  #modeApplied = false;
  #token = 0;

  onConnect() {
    if (this.dataset.sectionId && this.dataset.productId) {
      this.cleanup(() => product.abort(this.dataset.sectionId));
      this.subscribeLive(VARIANT_TOPIC, this.#onProduct);
    }

    this.#viewport = this.q('[data-viewport]');
    if (!this.#viewport) return;

    this.#wideMq = window.matchMedia(WIDE);
    this.#hoverMq = window.matchMedia(HOVER);
    this.#reduceMq = window.matchMedia(REDUCE);

    this.#cards = this.qa('[data-card]');
    this.#videos = new Map();
    for (const video of this.qa('[data-video]')) {
      this.#videos.set(this.#cardOf(video), video);
    }
    this.#toggles = new Map();
    for (const toggle of this.qa(`${TOGGLE_TAG}.workout-benefits__play`)) {
      this.#toggles.set(this.#cardOf(toggle), toggle);
    }
    this.#fills = new Map();
    for (const fill of this.qa('[data-fill]')) {
      this.#fills.set(this.#cardOf(fill), fill);
    }
    this.#storyFills = this.qa('[data-story-fill]');
    this.#storiesToggle = this.q(
      `${TOGGLE_TAG}.workout-benefits__stories-toggle`
    );
    this.#prev = this.q(`${TOGGLE_TAG}.workout-benefits__arrow--prev`);
    this.#next = this.q(`${TOGGLE_TAG}.workout-benefits__arrow--next`);

    this.#locked = new Set();
    this.#manual = new Set();
    this.#inView = new Set();
    this.#failed = new Set();
    this.#unplayable = new Set();
    this.#offScreen = true;
    this.#sectionShown = false;
    this.#hidden = document.hidden;
    this.#stories = false;
    this.#storyIndex = 0;
    this.#modeApplied = false;
    this.#resetPhotoClock();

    for (const [card, video] of this.#videos) {
      video.muted = true;
      this.listen(video, 'play', this.#onVideoPlay);
      this.listen(video, 'playing', this.#onVideoPlaying);
      this.listen(video, 'pause', this.#onVideoPause);
      this.listen(video, 'ended', this.#onVideoEnded);
      this.listen(video, 'error', this.#onMediaError, true);
      for (const type of LOAD_EVENTS) {
        this.listen(video, type, this.#syncStoryBuffering);
      }
      this.listen(card, 'pointerenter', this.#onPointerEnter);
      this.listen(card, 'pointerleave', this.#onPointerLeave);
    }
    for (const toggle of this.#toggles.values()) {
      this.listen(toggle, 'mousedown', this.#onControlMousedown);
      this.listen(toggle, 'focusin', this.#onControlChange);
      this.listen(toggle, 'transitionend', this.#onControlChange);
      this.listen(toggle, 'transitioncancel', this.#onControlChange);
    }

    this.listen(this.#viewport, 'keydown', this.#onKeydown);
    this.listen(this.#wideMq, 'change', this.#applyMode);
    this.listen(this.#hoverMq, 'change', this.#onHoverChange);
    this.listen(this.#reduceMq, 'change', this.#onReduceChange);
    this.listen(document, 'visibilitychange', this.#onVisibilityChange);

    this.observe(
      new IntersectionObserver(this.#onSectionVisible.bind(this), {
        threshold: [0, SECTION_IN_VIEW]
      }),
      this
    );

    const cardsInView = new IntersectionObserver(
      this.#onCardsVisible.bind(this),
      { root: this.#viewport, threshold: [0, CARD_IN_VIEW] }
    );
    for (const card of this.#videos.keys()) this.observe(cardsInView, card);

    this.cleanup(() => this.#destroySwiper());
    this.cleanup(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    });
    this.cleanup(() => {
      clearTimeout(this.#bufferingTimer);
      this.#bufferingTimer = 0;
    });

    this.#initSwiper();
    this.#applyMode();

    customElements.whenDefined(TOGGLE_TAG).then(() => this.#bindControls());
  }

  onDisconnect() {
    for (const video of this.#videos.values()) video.pause();
  }

  async #onProduct(state) {
    if (!state?.sections || !state.url) return;
    const token = ++this.#token;
    const { sectionId, productId } = this.dataset;
    if (String(state.productId) === productId) {
      product.abort(sectionId);
      return;
    }

    let html = null;
    try {
      html = await product.render({ url: state.url, sectionId });
    } catch (error) {
      if (isAbort(error)) return;
      console.error('[workout-benefits] product render failed:', error);
      return;
    }
    if (!this.isConnected || token !== this.#token) return;

    const fresh = sectionFragment({ [sectionId]: html }, sectionId, this.id);
    if (!fresh) {
      console.error('[workout-benefits] section missing in product render');
      return;
    }
    if (
      fresh.hidden === this.hidden &&
      fresh.dataset.mediaKey === this.dataset.mediaKey
    ) {
      this.dataset.productId = fresh.dataset.productId;
      return;
    }
    this.replaceWith(fresh);
  }

  #bindControls() {
    if (!this.isConnected) return;

    for (const [card, toggle] of this.#toggles) {
      toggle.toggleIcon({
        states: PLAY_STATES,
        onChange: () => this.#onCardToggle(card)
      });
      this.#syncCard(card);
    }

    this.#storiesToggle?.toggleIcon({
      states: PLAY_STATES,
      onChange: () => this.#toggleStories()
    });
    this.#syncStoriesToggle();
    this.#syncStoryBuffering();

    this.#prev?.run(() => this.#step(-1));
    this.#next?.run(() => this.#step(1));
  }

  #initSwiper(initialSlide = 0) {
    const loop = this.#canLoop();
    this.#swiper = new Swiper(this.#viewport, {
      initialSlide,
      slidesPerView: 'auto',
      spaceBetween: this.#readLength(GAP),
      loop,
      loopAdditionalSlides: this.#bleedSlides(),
      rewind: !loop,
      watchOverflow: true,
      watchSlidesProgress: true,
      speed: this.#slideSpeed(),
      on: {
        afterInit: instance => {
          this.#syncEdges(instance);
          this.#syncInert(instance);
        },
        progress: instance => this.#syncInert(instance),
        realIndexChange: instance => this.#onRealIndexChange(instance),
        transitionEnd: () => this.#reconcile(),
        click: (instance, event) => this.#onSwiperClick(event),
        lock: instance => this.#syncEdges(instance),
        unlock: instance => this.#syncEdges(instance),
        resize: instance => this.#onSwiperResize(instance)
      }
    });
  }

  #destroySwiper() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  #canLoop() {
    const count = this.#cards.length;
    const size = this.#viewport.clientWidth - this.#readLength(BLEED);
    const gap = this.#readLength(GAP);
    const slot = (this.#cards[0]?.getBoundingClientRect().width ?? 0) + gap;
    if (count < 2 || !size || slot <= gap) return false;
    let visible = 1;
    for (let index = 1; index < count; index += 1) {
      if (index * slot < size) visible += 1;
    }
    return count > visible + this.#bleedSlides();
  }

  #bleedSlides() {
    return this.#readLength(BLEED) > this.#readLength(GAP) ? 1 : 0;
  }

  #onSwiperResize(instance) {
    if (
      instance.params.loop !== this.#canLoop() ||
      instance.params.loopAdditionalSlides !== this.#bleedSlides()
    ) {
      queueMicrotask(() => {
        if (!this.isConnected || instance !== this.#swiper) return;
        const index = instance.realIndex;
        if (this.#stories) this.#resetStoryCard(this.#activeCard);
        this.#destroySwiper();
        this.#initSwiper(index);
        this.#storyIndex = this.#swiper.realIndex;
        this.#restartStory();
        this.#reconcile();
      });
      return;
    }
    this.#syncEdges(instance);
    this.#syncInert(instance);
    this.#reconcile();
  }

  #syncEdges(instance = this.#swiper) {
    if (!instance || instance.destroyed) return;
    this.toggleAttribute('data-no-scroll', instance.isLocked);
  }

  #syncInert(instance) {
    const visibleClass = instance.params.slideVisibleClass;
    for (const slide of instance.slides) {
      slide.toggleAttribute('inert', !slide.classList.contains(visibleClass));
    }
  }

  #step(direction) {
    if (!this.#swiper || this.#swiper.isLocked) return;
    if (direction > 0) this.#swiper.slideNext();
    else this.#swiper.slidePrev();
  }

  #onKeydown(event) {
    if (event.target !== this.#viewport) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!this.#swiper || this.#swiper.isLocked) return;
    event.preventDefault();
    const forward = this.#swiper.rtlTranslate
      ? event.key === 'ArrowLeft'
      : event.key === 'ArrowRight';
    this.#step(forward ? 1 : -1);
  }

  #slideSpeed() {
    return this.#reduceMq.matches ? 0 : SLIDE_SPEED;
  }

  #readLength(name) {
    return parseFloat(getComputedStyle(this).getPropertyValue(name)) || 0;
  }

  #applyMode() {
    const stories =
      this.dataset.mobile === 'stories' &&
      !this.#wideMq.matches &&
      this.#storyFills.length > 0;
    if (this.#modeApplied && stories === this.#stories) return;
    this.#modeApplied = true;

    this.#resetPhotoClock();
    for (const [card, video] of this.#videos) {
      video.pause();
      video.loop = !stories;
      this.#syncCard(card);
    }
    if (stories) {
      this.#locked.clear();
      this.#manual.clear();
      this.#storyUserPaused = this.#reduceMq.matches;
    }

    this.#stories = stories;
    this.#storyIndex = this.#swiper?.realIndex ?? 0;

    if (stories) this.#restartStory();
    else this.#paintStories(0);
    this.#syncStoriesToggle();
    this.#syncStoryBuffering();
    this.#reconcile();
  }

  #onHoverChange() {
    for (const [card, video] of this.#videos) {
      if (!this.#locked.has(card)) video.pause();
    }
    this.#manual.clear();
    this.#reconcile();
  }

  #onReduceChange() {
    if (this.#swiper) this.#swiper.params.speed = this.#slideSpeed();
    if (this.#reduceMq.matches) this.#storyUserPaused = true;
    this.#syncStoriesToggle();
    this.#onPlaybackContext();
  }

  #onVisibilityChange() {
    this.#hidden = document.hidden;
    this.#onPlaybackContext();
  }

  #onSectionVisible(entries) {
    const entry = entries.at(-1);
    if (!entry) return;
    this.#sectionShown = entry.isIntersecting;
    this.#offScreen = !(
      entry.isIntersecting && entry.intersectionRatio >= SECTION_IN_VIEW
    );
    this.#onPlaybackContext();
  }

  #onCardsVisible(entries) {
    for (const entry of entries) {
      if (entry.intersectionRatio >= CARD_IN_VIEW) {
        this.#inView.add(entry.target);
      } else {
        this.#inView.delete(entry.target);
      }
    }
    this.#reconcile();
  }

  #onPlaybackContext() {
    if (this.#stories) this.#startStory();
    else this.#reconcile();
  }

  #reconcile() {
    if (this.#stories) return;
    const onScreen = !this.#offScreen && !this.#hidden;
    const hover = this.#hoverMq.matches;
    const autoplay = onScreen && !hover && !this.#reduceMq.matches;

    for (const [card, video] of this.#videos) {
      if (this.#failed.has(card)) continue;
      if (!onScreen || !this.#isShown(card)) {
        video.pause();
        continue;
      }
      if (hover || this.#locked.has(card) || !video.paused) continue;
      if (this.#manual.has(card)) this.#play(card);
      else if (autoplay && !this.#unplayable.has(card)) this.#play(card);
    }
  }

  #isShown(card) {
    if (!this.#hoverMq.matches) return this.#inView.has(card);
    if (!this.#swiper) return true;
    return card.classList.contains(this.#swiper.params.slideVisibleClass);
  }

  #onPointerEnter(event) {
    if (event.pointerType !== 'mouse' || this.#stories) return;
    if (this.#reduceMq.matches) return;
    const card = event.currentTarget;
    if (this.#locked.has(card)) return;
    this.#play(card);
  }

  #onPointerLeave(event) {
    if (event.pointerType !== 'mouse' || this.#stories) return;
    const card = event.currentTarget;
    if (this.#locked.has(card)) return;
    this.#videos.get(card)?.pause();
  }

  #onControlMousedown(event) {
    event.preventDefault();
  }

  #onControlChange(event) {
    if (event.type !== 'focusin' && event.target !== event.currentTarget) {
      return;
    }
    this.#syncCard(this.#cardOf(event.currentTarget));
  }

  #onSwiperClick(event) {
    if (!event?.target || event.target.closest(TOGGLE_TAG)) return;
    const card = this.#cardOf(event.target);
    if (!card) return;
    if (this.#stories) {
      this.#toggleStories();
      return;
    }
    if (event.pointerType === 'mouse' || !this.#videos.has(card)) return;
    this.#onCardToggle(card);
  }

  #onCardToggle(card) {
    const video = this.#videos.get(card);
    if (!video || this.#failed.has(card)) return;
    if (video.paused) {
      this.#locked.delete(card);
      this.#manual.add(card);
      this.#play(card);
    } else {
      this.#locked.add(card);
      this.#manual.delete(card);
      video.pause();
    }
    this.#syncCard(card);
  }

  #play(card) {
    const video = this.#videos.get(card);
    if (!video || this.#failed.has(card) || !this.isConnected) return;
    video.muted = true;
    video.play().then(null, error => this.#onPlayRejected(error, card));
  }

  #onPlayRejected(error, card) {
    if (!this.isConnected) return;
    this.#syncCard(card);
    if (isAbort(error)) return;
    if (error.name === 'NotAllowedError') {
      this.#unplayable.add(card);
      if (this.#stories && card === this.#activeCard) this.#startStory();
      return;
    }
    console.error('[workout-benefits] playback error:', error);
    this.#degrade(card);
  }

  #onMediaError(event) {
    const card = this.#cardOf(event.target);
    const video = this.#videos.get(card);
    if (!video) return;
    if (
      event.target.tagName === 'SOURCE' &&
      video.networkState !== HTMLMediaElement.NETWORK_NO_SOURCE
    ) {
      return;
    }
    console.error('[workout-benefits] video failed to load:', video.error);
    this.#degrade(card);
  }

  #degrade(card) {
    if (this.#failed.has(card)) return;
    this.#failed.add(card);
    card.dataset.failed = '';
    delete card.dataset.started;
    this.#videos.get(card)?.pause();
    this.#syncCard(card);
    if (this.#stories && card === this.#activeCard) this.#startStory();
  }

  #onVideoPlay(event) {
    this.#syncCard(this.#cardOf(event.target));
    this.#requestFrame();
  }

  #onVideoPlaying(event) {
    const card = this.#cardOf(event.target);
    if (!card) return;
    card.dataset.started = '';
    this.#unplayable.delete(card);
    this.#syncCard(card);
    this.#requestFrame();
  }

  #onVideoPause(event) {
    const card = this.#cardOf(event.target);
    this.#syncCard(card);
    this.#paintLine(card);
    this.#syncStoryBuffering();
  }

  #onVideoEnded(event) {
    if (!this.#stories) return;
    if (this.#cardOf(event.target) !== this.#activeCard) return;
    this.#advance();
  }

  #syncCard(card) {
    const video = this.#videos.get(card);
    if (!video) return;
    const playing = !video.paused;
    let state = '';
    if (playing) state = PLAYING;
    else if (this.#locked.has(card)) state = 'locked';
    else if (card.dataset.started !== undefined) state = PAUSED;

    if (state) card.dataset.state = state;
    else delete card.dataset.state;

    const toggle = this.#toggles.get(card);
    if (!toggle?.setIconState) return;
    if (!playing && this.#isFadingOut(card, toggle)) return;
    toggle.setIconState(playing ? PLAYING : PAUSED);
  }

  #isFadingOut(card, toggle) {
    if (!this.#hoverMq.matches || this.#locked.has(card)) return false;
    if (card.matches(':hover') || toggle.matches(':focus-within')) return false;
    return parseFloat(getComputedStyle(toggle).opacity) > 0;
  }

  get #activeCard() {
    return this.#cards[this.#storyIndex] ?? null;
  }

  get #storyWanted() {
    return (
      this.#stories &&
      !this.#storyUserPaused &&
      !this.#hidden &&
      this.#sectionShown
    );
  }

  get #storyPlaying() {
    return this.#storyWanted && !this.#offScreen;
  }

  #usesPhotoClock(card) {
    return (
      !this.#videos.has(card) ||
      this.#failed.has(card) ||
      this.#unplayable.has(card)
    );
  }

  #startStory() {
    if (!this.#stories) return;
    this.#stallTime = -1;
    const card = this.#activeCard;
    if (!card) return;
    const video = this.#videos.get(card);
    if (video && this.#storyWanted && !this.#usesPhotoClock(card)) {
      video.preload = 'auto';
    }

    if (!this.#storyPlaying) {
      video?.pause();
      this.#pausePhotoClock();
      this.#paintStories(this.#storyProgress());
      this.#syncStoryBuffering();
      return;
    }

    if (this.#usesPhotoClock(card)) {
      video?.pause();
      if (!this.#photoStartedAt) {
        this.#photoStartedAt = performance.now() - this.#photoElapsed;
      }
    } else {
      this.#play(card);
    }
    this.#syncStoryBuffering();
    this.#requestFrame();
  }

  #restartStory() {
    if (!this.#stories) return;
    this.#pendingAdvance = false;
    this.#stallTime = -1;
    this.#unplayable.delete(this.#activeCard);
    this.#resetStoryCard(this.#activeCard);
    this.#resetPhotoClock();
    this.#paintStories(0);
    this.#startStory();
  }

  #onRealIndexChange(instance) {
    if (!this.#stories) return;
    const outgoing = this.#activeCard;
    this.#resetStoryCard(outgoing);
    const video = this.#videos.get(outgoing);
    if (video) video.preload = 'metadata';
    this.#storyIndex = instance.realIndex;
    this.#restartStory();
  }

  #resetStoryCard(card) {
    const video = this.#videos.get(card);
    if (!video) return;
    video.pause();
    if (video.currentTime > 0) video.currentTime = 0;
    delete card.dataset.started;
    this.#syncCard(card);
  }

  #advance() {
    if (!this.#swiper || this.#swiper.isLocked || this.#cards.length < 2) {
      this.#restartStory();
      return;
    }
    if (this.#swiper.slideNext()) {
      this.#advanceRetries = 0;
      return;
    }
    this.#advanceRetries += 1;
    if (this.#advanceRetries > ADVANCE_RETRIES) {
      this.#advanceRetries = 0;
      this.#restartStory();
      return;
    }
    this.#pendingAdvance = true;
    this.#requestFrame();
  }

  #checkStall(card, video) {
    if (!this.#storyPlaying || this.#usesPhotoClock(card)) {
      this.#stallTime = -1;
      return;
    }
    const now = performance.now();
    if (video.currentTime !== this.#stallTime) {
      this.#stallTime = video.currentTime;
      this.#stallSince = now;
      return;
    }
    if (now - this.#stallSince < STALL_TIMEOUT) return;
    this.#stallTime = -1;
    this.#unplayable.add(card);
    this.#startStory();
  }

  #toggleStories() {
    if (!this.#stories) return;
    this.#storyUserPaused = !this.#storyUserPaused;
    this.#syncStoriesToggle();
    this.#startStory();
  }

  #storyBuffering() {
    if (!this.#storyWanted) return false;
    const card = this.#activeCard;
    const video = this.#videos.get(card);
    if (!video || this.#usesPhotoClock(card) || video.ended) return false;
    if (
      video.paused &&
      video.networkState !== HTMLMediaElement.NETWORK_LOADING
    ) {
      return false;
    }
    return video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA;
  }

  #syncStoryBuffering() {
    const buffering = this.#storyBuffering();
    const now = performance.now();
    if (!buffering) this.#bufferingSince = 0;
    else if (!this.#bufferingSince) this.#bufferingSince = now;
    const elapsed = now - this.#bufferingSince;
    const shown = buffering && elapsed >= BUFFERING_DELAY;
    if (buffering && !shown && !this.#bufferingTimer) {
      this.#bufferingTimer = setTimeout(() => {
        this.#bufferingTimer = 0;
        if (this.isConnected) this.#syncStoryBuffering();
      }, BUFFERING_DELAY - elapsed);
    }
    this.#storiesToggle?.toggleAttribute('data-buffering', shown);
    return buffering;
  }

  #syncStoriesToggle() {
    this.#storiesToggle?.setIconState?.(
      this.#storyUserPaused ? PAUSED : PLAYING
    );
  }

  #pausePhotoClock() {
    if (!this.#photoStartedAt) return;
    this.#photoElapsed = performance.now() - this.#photoStartedAt;
    this.#photoStartedAt = 0;
  }

  #resetPhotoClock() {
    this.#photoElapsed = 0;
    this.#photoStartedAt = 0;
  }

  #storyProgress() {
    const card = this.#activeCard;
    if (!card) return 0;
    if (this.#usesPhotoClock(card)) {
      const elapsed = this.#photoStartedAt
        ? performance.now() - this.#photoStartedAt
        : this.#photoElapsed;
      return Math.min(elapsed / PHOTO_DURATION, 1);
    }
    return this.#videoProgress(this.#videos.get(card));
  }

  #videoProgress(video) {
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
      return 0;
    }
    return Math.min(video.currentTime / video.duration, 1);
  }

  #paintStories(progress) {
    this.#storyFills.forEach((fill, index) => {
      let value = 0;
      if (index < this.#storyIndex) value = 1;
      else if (index === this.#storyIndex) value = progress;
      fill.style.setProperty(PROGRESS, value);
    });
  }

  #paintLine(card) {
    const fill = this.#fills.get(card);
    if (!fill) return;
    fill.style.setProperty(
      PROGRESS,
      this.#videoProgress(this.#videos.get(card))
    );
  }

  #requestFrame() {
    if (this.#frame || !this.isConnected) return;
    this.#frame = requestAnimationFrame(() => this.#onFrame());
  }

  #onFrame() {
    this.#frame = 0;
    if (!this.isConnected) return;

    let running = false;
    for (const [card, video] of this.#videos) {
      if (video.paused) continue;
      running = true;
      this.#paintLine(card);
    }

    if (this.#stories && this.#activeCard) {
      if (this.#pendingAdvance) {
        this.#pendingAdvance = false;
        this.#advance();
        return;
      }
      const card = this.#activeCard;
      const video = this.#videos.get(card);
      if (video && !video.paused) this.#checkStall(card, video);
      const buffering = this.#syncStoryBuffering();
      const progress = this.#storyProgress();
      if (!buffering) this.#paintStories(progress);
      if (this.#photoStartedAt) {
        if (progress >= 1) {
          this.#advance();
          return;
        }
        running = true;
      }
    }

    if (running) this.#requestFrame();
  }

  #cardOf(node) {
    return node?.closest?.('[data-card]') ?? null;
  }
}

if (!customElements.get('workout-benefits')) {
  customElements.define('workout-benefits', WorkoutBenefits);
}
